import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRightLeft, Trash2 } from "lucide-react";

import {
  AssetsService,
  type AssetType,
  type LifecycleState,
  type RiskClassification,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { LIFECYCLE_TONE, RISK_TONE } from "@/lib/asset-enums";
import { formatDateID, formatRelativeTimeID } from "@/lib/format";
import { ROUTES } from "@/lib/constants";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Breadcrumb } from "@/components/shared/breadcrumb";
import { PageHeader } from "@/components/shared/page-header";
import { Section } from "@/components/shared/section";
import { AssetDeleteDialog } from "@/components/assets/asset-delete-dialog";
import { AssetTransitionDialog } from "@/components/assets/asset-transition-dialog";

// Enum values arrive from the backend; map each known value to a
// translation key so the badges re-render on a locale switch.
const ASSET_TYPE_KEY: Record<AssetType, TranslationKey> = {
  prompt: "assets.types.prompt",
  model: "assets.types.model",
  agent: "assets.types.agent",
  workflow: "assets.types.workflow",
  provider: "assets.types.provider",
  tool: "assets.types.tool",
  dataset: "assets.types.dataset",
};

const LIFECYCLE_KEY: Record<LifecycleState, TranslationKey> = {
  draft: "assets.status.draft",
  review: "assets.status.review",
  active: "assets.status.active",
  deprecated: "assets.status.deprecated",
  retired: "assets.status.retired",
};

const RISK_KEY: Record<RiskClassification, TranslationKey> = {
  low: "assets.risk.low",
  medium: "assets.risk.medium",
  high: "assets.risk.high",
  critical: "assets.risk.critical",
};

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const detailFetcher = useCallback(() => {
    if (!id) throw new Error(t("assets.page.missingId"));
    return AssetsService.get(id);
  }, [id, t]);

  const historyFetcher = useCallback(() => {
    if (!id) throw new Error(t("assets.page.missingId"));
    return AssetsService.getHistory(id, { limit: 20 });
  }, [id, t]);

  const detail = useApi(detailFetcher, { deps: [id] });
  const history = useApi(historyFetcher, { deps: [id] });

  const asset = detail.data?.base ?? null;

  const handleTransitionSuccess = () => {
    setTransitionOpen(false);
    void detail.refetch();
    void history.refetch();
  };

  const handleDeleteSuccess = () => {
    setDeleteOpen(false);
    navigate(ROUTES.ASSETS);
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[
          { label: t("assets.page.title"), to: ROUTES.ASSETS },
          { label: asset?.name ?? t("assets.page.breadcrumbLoading") },
        ]}
      />

      {detail.error && (
        <Alert variant="danger">{parseApiError(detail.error)}</Alert>
      )}

      {detail.isLoading && !asset ? (
        <Skeleton className="h-12 w-1/2" />
      ) : asset ? (
        <>
          <PageHeader
            title={asset.name}
            description={asset.description ?? undefined}
            action={
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setTransitionOpen(true)} size="sm">
                  <ArrowRightLeft size={14} />
                  {t("assets.actions.transition")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 size={14} />
                  {t("assets.actions.delete")}
                </Button>
              </div>
            }
          />

          <div className="grid grid-cols-2 gap-3 rounded-lg border border-ocean-100 bg-white px-4 py-3 text-sm desktop:grid-cols-4">
            <Field label={t("assets.detail.fieldType")}>
              <Badge tone="ocean">
                {t(ASSET_TYPE_KEY[asset.asset_type])}
              </Badge>
            </Field>
            <Field label={t("assets.detail.fieldLifecycle")}>
              <Badge tone={LIFECYCLE_TONE[asset.lifecycle_state]}>
                {t(LIFECYCLE_KEY[asset.lifecycle_state])}
              </Badge>
            </Field>
            <Field label={t("assets.detail.fieldRisk")}>
              <Badge tone={RISK_TONE[asset.risk_classification]}>
                {t(RISK_KEY[asset.risk_classification])}
              </Badge>
            </Field>
            <Field label={t("assets.detail.fieldUpdated")}>
              {formatDateID(asset.updated_at)}
            </Field>
            <Field label={t("assets.detail.fieldSlug")}>
              <code className="font-mono text-xs text-neutral-600">
                {asset.slug}
              </code>
            </Field>
            <Field label={t("assets.detail.fieldTags")}>
              {asset.tags.length === 0 ? (
                <span className="text-neutral-400">—</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {asset.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded bg-ocean-50 px-1.5 py-0.5 text-[10px]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </Field>
            <Field label={t("assets.detail.fieldCoOwners")}>
              {asset.co_owners.length === 0 ? (
                <span className="text-neutral-400">—</span>
              ) : (
                t("assets.detail.coOwnersCount", {
                  count: asset.co_owners.length,
                })
              )}
            </Field>
            <Field label={t("assets.detail.fieldDependencies")}>
              {(detail.data?.dependencies_out?.length ?? 0) +
                (detail.data?.dependencies_in?.length ?? 0) === 0 ? (
                <span className="text-neutral-400">—</span>
              ) : (
                t("assets.detail.dependenciesCount", {
                  out: detail.data?.dependencies_out?.length ?? 0,
                  in: detail.data?.dependencies_in?.length ?? 0,
                })
              )}
            </Field>
          </div>
        </>
      ) : (
        !detail.isLoading && (
          <Alert variant="warning">{t("assets.page.notFound")}</Alert>
        )
      )}

      <Section
        title={t("assets.detail.historyTitle")}
        description={t("assets.detail.historyDescription")}
      >
        {history.error ? (
          <Alert variant="warning">{parseApiError(history.error)}</Alert>
        ) : history.isLoading && !history.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("assets.detail.historyColTime")}</TableHead>
                  <TableHead>
                    {t("assets.detail.historyColChangeType")}
                  </TableHead>
                  <TableHead className="hidden tablet:table-cell">
                    {t("assets.detail.historyColChangedBy")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!history.data || history.data.items.length === 0 ? (
                  <TableEmpty
                    colSpan={3}
                    message={t("assets.detail.historyEmpty")}
                  />
                ) : (
                  history.data.items.map((change) => (
                    <TableRow key={change.id}>
                      <TableCell className="text-neutral-600">
                        {formatRelativeTimeID(change.created_at)}
                      </TableCell>
                      <TableCell className="font-medium text-ocean-900">
                        {change.change_type}
                      </TableCell>
                      <TableCell className="hidden font-mono text-xs text-neutral-600 tablet:table-cell">
                        {change.changed_by_user_id ??
                          t("assets.detail.systemActor")}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <AssetTransitionDialog
        asset={transitionOpen ? asset : null}
        onClose={() => setTransitionOpen(false)}
        onSuccess={handleTransitionSuccess}
      />

      <AssetDeleteDialog
        asset={deleteOpen ? asset : null}
        onClose={() => setDeleteOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-ocean-900">{children}</div>
    </div>
  );
}
